import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowRight, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { WishlistContext } from "../context/WishlistContext";
import { useSampleRequest } from "../context/SampleRequestContext";

/**
 * Wishlist drawer - shows saved lots
 * Controlled by parent via open / onOpenChange.
 */
export const WishlistDrawer = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const { wishlist, wishlistCount, removeFromWishlist } = useContext(WishlistContext);
  const { openModal } = useSampleRequest();

  const handleBrowse = () => {
    onOpenChange?.(false);
    navigate("/collection");
  };

  const handleRequestSamples = () => {
    onOpenChange?.(false);
    // Pass all wishlisted lots to the modal
    openModal(wishlist, "wishlist_drawer");
  };

  const handleRemoveItem = (e, lotId) => {
    e.stopPropagation();
    removeFromWishlist(lotId);
  };

  const handleViewLot = (item) => {
    onOpenChange?.(false);
    navigate(`/collection/${item.typeSlug}/lot/${item.lotId}`);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="wishlist-drawer"
        className="w-full sm:max-w-[420px] border-l border-white/10 bg-brand-charcoal text-brand-cream p-0 flex flex-col"
      >
        <SheetHeader className="px-8 pt-8 pb-6 border-b border-white/10 text-left">
          <SheetTitle
            data-testid="wishlist-drawer-title"
            className="font-serif text-3xl font-light tracking-tight text-brand-cream"
          >
            Your Wishlist
          </SheetTitle>
          <p className="font-sans text-xs uppercase tracking-[0.22em] text-brand-cream/40">
            {wishlistCount === 0 ? "Curated marble · Saved for later" : `${wishlistCount} lot${wishlistCount === 1 ? "" : "s"} saved`}
          </p>
        </SheetHeader>

        {wishlistCount === 0 ? (
          // Empty State
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="relative mb-8">
              <div className="absolute inset-0 rounded-full bg-brand-gold/5 blur-2xl" />
              <div className="relative h-20 w-20 rounded-full border border-white/10 flex items-center justify-center">
                <Heart className="h-8 w-8 text-brand-gold/70" strokeWidth={1.2} />
              </div>
            </div>

            <h3 className="font-serif text-2xl text-brand-cream mb-3">
              Your wishlist is empty
            </h3>
            <p className="font-sans text-sm text-brand-cream/60 max-w-xs leading-relaxed mb-10">
              Start exploring the collection - save the marble lots that move you.
            </p>

            <button
              type="button"
              onClick={handleBrowse}
              data-testid="wishlist-browse-collection-btn"
              className="group inline-flex items-center gap-3 bg-brand-gold px-7 py-3.5 text-brand-charcoal font-sans text-xs uppercase tracking-[0.22em] font-medium transition-colors hover:bg-brand-cream"
            >
              Browse Collection
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
            </button>
          </div>
        ) : (
          // Wishlist Items
          <>
            <div className="flex-1 overflow-y-auto px-8 py-6">
              <div className="space-y-4">
                {wishlist.map((item) => (
                  <div
                    key={item.lotId}
                    data-testid={`wishlist-item-${item.lotId}`}
                    onClick={() => handleViewLot(item)}
                    className="group relative flex gap-4 p-3 bg-brand-surface/30 hover:bg-brand-surface/50 ring-1 ring-white/5 hover:ring-brand-gold/30 transition-all cursor-pointer"
                    style={{ borderRadius: "5px" }}
                  >
                    {/* Thumbnail */}
                    <div className="relative w-20 h-24 flex-shrink-0 overflow-hidden">
                      <img
                        src={item.coverImage}
                        alt={item.lotNumber || `Lot ${item.lotId}`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-lg text-brand-cream mb-1">
                        {item.lotNumber || `Lot ${item.lotId}`} — {item.typeName}
                      </h4>
                      {item.thickness && (
                        <p className="font-sans text-sm text-brand-cream/65 mb-2">
                          {item.thickness}
                        </p>
                      )}
                    </div>

                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveItem(e, item.lotId)}
                      data-testid={`wishlist-remove-${item.lotId}`}
                      aria-label="Remove from wishlist"
                      className="absolute top-3 right-3 p-1.5 text-brand-cream/40 hover:text-brand-cream/80 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer with CTA */}
            <div className="px-8 py-6 border-t border-white/10">
              <button
                type="button"
                onClick={handleRequestSamples}
                data-testid="wishlist-request-all-btn"
                className="w-full inline-flex items-center justify-center gap-3 bg-brand-gold px-6 py-4 text-brand-charcoal font-sans text-sm uppercase tracking-[0.2em] font-medium transition-colors hover:bg-brand-gold/90"
                style={{ borderRadius: "5px" }}
              >
                Request Samples for All Saved Lots
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
          </>
        )}

        <div className="px-8 py-6 border-t border-white/10 text-center">
          <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-brand-cream/30">
            Aurevia · The quarry, at your doorstep
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default WishlistDrawer;
